import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Upload, Trash2, UserPlus, X } from 'lucide-react';
import { API, Storage } from 'aws-amplify';
import * as mutationsModule from '../../../graphql/mutations';
import fetchTeam from '../../../application/routes/teams/fetchTeam';
import fetchTeamMembers from '../../../application/routes/teams/fetchTeamMembers';
import fetchTeamLevels from '../../../application/routes/teams/fetchTeamLevels';
import * as utils from '../../../application/common/Utilities';
import { useCurrentUserStore } from '../../../application/store/zustand/useCurrentUserStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { FormField } from '../../components/ui/form-field';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';
import { AddMembersModal } from './AddMembersModal';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
  appSettings: {
    dateFormat: string;
    labels: {
      circle: { singular: string; plural: string };
    };
  };
};

interface TeamLevel {
  label: string;
  value: string;
  levelNum: number;
}

interface FormValues {
  id: string;
  name: string;
  description: string;
  imageName: string;
  imageType: string;
  avatarImageName: string;
  orgID: string;
  levelID: string;
  scope: string;
  inviterType: string;
  approverType: string;
  startDate: string;
  endDate: string;
  launchDate: string;
  isHidden: boolean;
  extID: string;
  isLeague: boolean;
  status: string;
  isActive: boolean;
}

const defaultValues: FormValues = {
  id: '',
  name: '',
  description: '',
  imageName: '',
  imageType: '',
  avatarImageName: '',
  orgID: '',
  levelID: '',
  scope: 'public',
  inviterType: 'anyone',
  approverType: 'auto',
  startDate: '',
  endDate: '',
  launchDate: '',
  isHidden: false,
  extID: '',
  isLeague: false,
  status: '',
  isActive: true,
};

export function TeamFormPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    addCurrentUserTeams,
    updateCurrentUserTeams,
    removeCurrentUserTeams,
    removeMembersFromCurrentUserTeam,
  } = useCurrentUserStore();

  const isEditing = Boolean(teamId);

  const [values, setValues] = useState<FormValues>({
    ...defaultValues,
    orgID: global.me.orgID,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [teamLevels, setTeamLevels] = useState<TeamLevel[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);

  useEffect(() => {
    prepareFormData();
  }, [teamId]);

  const prepareFormData = async () => {
    setIsFormLoading(true);
    try {
      const levels = await fetchTeamLevels();
      setTeamLevels(levels);

      if (teamId) {
        const team = await fetchTeam(teamId);
        if (team) {
          setValues({
            id: team.id || '',
            name: team.name || '',
            description: team.description || '',
            imageName: team.imageName || '',
            imageType: team.imageType || '',
            avatarImageName: team.avatarImageName || '',
            orgID: team.orgID || global.me.orgID,
            levelID: team.levelID || '',
            scope: team.scope || 'public',
            inviterType: team.inviterType || 'anyone',
            approverType: team.approverType || 'auto',
            startDate: team.startDate
              ? utils.defaultDateFormat(team.startDate)
              : '',
            endDate: team.endDate ? utils.defaultDateFormat(team.endDate) : '',
            launchDate: team.launchDate
              ? utils.defaultDateFormat(team.launchDate)
              : '',
            isHidden: team.isHidden || false,
            extID: team.extID || '',
            isLeague: team.isLeague || false,
            status: team.status || '',
            isActive: team.isActive !== undefined ? team.isActive : true,
          });

          if (team.avatarFile) {
            setImagePreviewUrl(team.avatarFile);
          }

          const teamMembers = await fetchTeamMembers(teamId);
          const activeMembers = (teamMembers || []).filter(
            (m: any) => m.status === 'active'
          );
          setMembers(activeMembers);
        }
      }
    } catch (err) {
      console.error('Error preparing form data:', err);
      toast.error('Failed to load form data');
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleChange = (field: keyof FormValues, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!values.name || values.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    if (values.name && values.name.length > 32) {
      newErrors.name = 'Name must be less than 32 characters';
    }
    if (values.description && values.description.length > 1024) {
      newErrors.description = 'Description must be less than 1024 characters';
    }
    if (!values.levelID) {
      newErrors.levelID = 'Please select a team level';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error('Please fix all errors and try again');
      return;
    }

    setIsSaving(true);
    try {
      if (isEditing) {
        await updateTeam();
        toast.success(`${values.name} team updated successfully`);
      } else {
        await createTeam();
        toast.success(`${values.name} team created successfully`);
      }
      navigate('/v2/teams');
    } catch (err: any) {
      console.error('Save error:', err);
      toast.error(
        `There was an error saving your ${
          global.appSettings?.labels?.circle?.singular || 'team'
        }.`
      );
    } finally {
      setIsSaving(false);
    }
  };

  const updateTeam = async () => {
    const input = {
      id: values.id,
      name: values.name,
      description: values.description,
      imageName: values.imageName,
      avatarImageName: values.avatarImageName,
      imageType: values.imageType,
      startDate: utils.graphqlDateFormat(
        utils.defaultIfEmpty(values.startDate, utils.MIN_DATE)
      ),
      endDate: utils.graphqlDateFormat(
        utils.defaultIfEmpty(values.endDate, utils.MAX_DATE)
      ),
      launchDate: utils.graphqlDateFormat(
        utils.defaultIfEmpty(
          values.launchDate,
          utils.defaultIfEmpty(values.startDate, utils.MIN_DATE)
        )
      ),
      orgID: values.orgID,
      levelID: values.levelID,
      scope: values.scope,
      inviterType: values.inviterType,
      approverType: values.approverType,
      isHidden: values.isHidden,
      extID: values.extID,
      isLeague: values.isLeague,
      status: values.status,
      isActive: values.isActive,
    };

    await API.graphql({
      query: (mutationsModule as any).updateCircle,
      variables: { input },
    });

    const isCurrentUserOnTeam = members.some((m) => m.id === global.me.id);
    const storeTeam = {
      id: values.id,
      name: values.name,
      imageName: values.imageName,
      isDeleted: false,
      status: values.status,
      users: members.reduce((acc: any, member: any) => {
        acc[member.id] = member;
        return acc;
      }, {}),
    };

    if (isCurrentUserOnTeam) {
      updateCurrentUserTeams([storeTeam]);
    } else {
      removeCurrentUserTeams([values.id]);
    }
  };

  const createTeam = async () => {
    const input = {
      name: values.name,
      description: values.description,
      scope: values.scope,
      inviterType: values.inviterType,
      approverType: values.approverType,
      status: 'created',
      isActive: true,
      imageName: values.imageName,
      avatarImageName: values.avatarImageName,
      imageType: values.imageType,
      communityID: '',
      startDate: utils.graphqlDateFormat(
        utils.defaultIfEmpty(values.startDate, utils.MIN_DATE)
      ),
      endDate: utils.graphqlDateFormat(
        utils.defaultIfEmpty(values.endDate, utils.MAX_DATE)
      ),
      launchDate: utils.graphqlDateFormat(
        utils.defaultIfEmpty(
          values.launchDate,
          utils.defaultIfEmpty(values.startDate, utils.MIN_DATE)
        )
      ),
      orgID: values.orgID,
      isDeleted: false,
      levelID: values.levelID,
      isHidden: values.isHidden,
      extID: values.name,
      isLeague: values.isLeague,
    };

    const res: any = await API.graphql({
      query: (mutationsModule as any).createCircle,
      variables: { input },
    });
    const teamData = res.data.createCircle;

    // Add members to new team
    if (members.length) {
      for (const user of members) {
        await API.graphql({
          query: (mutationsModule as any).modifyCircleMember,
          variables: {
            circleID: teamData.id,
            personID: user.id,
            status: 'accepted',
            role: '',
          },
        });
      }
    }

    // Update current user store
    const isCurrentUserOnTeam = members.some((m) => m.id === global.me.id);
    if (isCurrentUserOnTeam) {
      const storeTeam = {
        id: teamData.id,
        name: teamData.name,
        imageName: teamData.imageName,
        isDeleted: teamData.isDeleted,
        status: teamData.status,
        users: members.reduce((acc: any, member: any) => {
          acc[member.id] = member;
          return acc;
        }, {}),
      };
      addCurrentUserTeams([storeTeam]);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const formats = ['jpeg', 'jpg', 'png', 'gif'];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!formats.includes(ext)) {
      toast.error('Image format must be png, gif, or jpeg');
      return;
    }

    setIsImageLoading(true);
    try {
      const randomString = utils.generateRandomImageFileNameString();
      const newImageName = `${randomString}.${ext}`;

      const uploaded: any = await Storage.put(newImageName, file, {
        contentType: file.type,
      });

      handleChange('imageName', uploaded.key);
      handleChange('imageType', file.type);

      const previewUrl = URL.createObjectURL(file);
      setImagePreviewUrl(previewUrl);
    } catch (err) {
      console.error('Image upload error:', err);
      toast.error('Failed to upload image');
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleDeleteImage = () => {
    handleChange('imageName', '');
    handleChange('imageType', '');
    setImagePreviewUrl(null);
  };

  const handleAddMembers = async (newMembers: any[]) => {
    // If editing, add members to the team immediately
    if (isEditing && teamId) {
      try {
        for (const user of newMembers) {
          await API.graphql({
            query: (mutationsModule as any).modifyCircleMember,
            variables: {
              circleID: teamId,
              personID: user.id,
              status: 'accepted',
              role: '',
            },
          });
        }
      } catch (err) {
        console.error('Error adding members:', err);
        toast.error('Failed to add some members');
        return;
      }
    }

    setMembers((prev) => [...prev, ...newMembers]);
    setIsDirty(true);

    if (newMembers.length === 1) {
      toast.success(`${newMembers[0].name} added successfully`);
    } else {
      toast.success(`${newMembers.length} members added successfully`);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (isEditing && teamId) {
      if (members.length === 1) {
        toast.error('You must have at least one member on a team');
        return;
      }
      try {
        await API.graphql({
          query: (mutationsModule as any).modifyCircleMember,
          variables: {
            circleID: teamId,
            personID: memberId,
            status: 'removed',
            role: '',
          },
        });
        removeMembersFromCurrentUserTeam([memberId], teamId);
      } catch (err) {
        console.error('Remove member error:', err);
        toast.error('Failed to remove member');
        return;
      }
    }
    setMembers((prev) => prev.filter((m) => m.id !== memberId));
    toast.success('Member removed');
  };

  const handleCancel = () => {
    if (isDirty) {
      if (
        window.confirm(
          'Leave without saving changes? Your progress will be lost.'
        )
      ) {
        navigate('/v2/teams');
      }
    } else {
      navigate('/v2/teams');
    }
  };

  if (isFormLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 md:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleCancel}>
            <ArrowLeft className="size-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground">
            {isEditing ? 'Edit Team' : 'New Team'}
          </h1>
        </div>
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
          {isEditing ? 'Update team' : 'Save changes'}
        </Button>
      </div>

      {/* Form */}
      <div className="flex-1 p-4 md:p-6 max-w-2xl mx-auto w-full space-y-6">
        {/* Image Upload */}
        <div className="flex flex-col items-center">
          {isImageLoading ? (
            <div className="size-40 rounded-full bg-muted flex items-center justify-center">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : imagePreviewUrl ? (
            <div className="relative">
              <img
                src={imagePreviewUrl}
                alt="Team"
                className="size-40 rounded-full object-cover border-2 border-border"
              />
              <div className="absolute bottom-1 right-1 flex gap-1">
                <label className="size-8 rounded-full bg-card border border-border shadow-sm flex items-center justify-center cursor-pointer hover:bg-muted transition-colors">
                  <Upload className="size-3.5" />
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </label>
                <button
                  onClick={handleDeleteImage}
                  className="size-8 rounded-full bg-card border border-border shadow-sm flex items-center justify-center hover:bg-muted transition-colors"
                >
                  <Trash2 className="size-3.5 text-destructive" />
                </button>
              </div>
            </div>
          ) : (
            <label className="size-40 rounded-full bg-primary/10 border-2 border-dashed border-primary/30 flex flex-col items-center justify-center cursor-pointer hover:bg-primary/15 transition-colors">
              <Upload className="size-6 text-primary mb-1" />
              <span className="text-sm font-medium text-primary">
                Add Image
              </span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif"
                className="hidden"
                onChange={handleImageUpload}
              />
            </label>
          )}
        </div>

        {/* About Section */}
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="bg-muted/50 px-4 py-3 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">About</h2>
          </div>
          <div className="p-4 space-y-4">
            <FormField label="*Name" error={errors.name}>
              <Input
                value={values.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Name"
              />
            </FormField>

            <FormField label="Description" error={errors.description}>
              <Textarea
                value={values.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Description"
                rows={3}
              />
            </FormField>

            <FormField label="*Team Level" error={errors.levelID}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {teamLevels.map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => handleChange('levelID', level.value)}
                    className={cn(
                      'px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors',
                      values.levelID === level.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-foreground hover:bg-muted/50'
                    )}
                  >
                    {level.label}
                  </button>
                ))}
              </div>
            </FormField>
          </div>
        </div>

        {/* Members Section */}
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="bg-muted/50 px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">
              Members ({members.length})
            </h2>
            <UserPlus className="size-4 text-muted-foreground" />
          </div>
          <div className="p-4 space-y-3">
            {/* Add members button */}
            <button
              type="button"
              onClick={() => setShowAddMembersModal(true)}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors border border-primary/10"
            >
              <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <UserPlus className="size-4 text-primary" />
              </div>
              <span className="text-sm font-medium text-primary">
                Add members
              </span>
            </button>

            {/* Member list */}
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors"
              >
                {member.avatarFile ? (
                  <img
                    src={member.avatarFile}
                    alt={member.name}
                    className="size-9 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-xs font-semibold text-primary">
                      {member.initials || member.name?.[0]?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {member.name}
                  </p>
                  {member.title && (
                    <p className="text-xs text-muted-foreground truncate">
                      {member.title}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveMember(member.id)}
                  className="size-7 rounded-full flex items-center justify-center hover:bg-destructive/10 transition-colors"
                >
                  <X className="size-3.5 text-destructive" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom spacer */}
        <div className="h-8" />
      </div>

      {/* Add Members Modal */}
      <AddMembersModal
        open={showAddMembersModal}
        onClose={() => setShowAddMembersModal(false)}
        onSubmit={handleAddMembers}
        existingMemberIds={members.map((m) => m.id)}
      />
    </div>
  );
}
